<template>
  <div class="privacy-container">
    <h2 class="title">{{appName}} User Privacy Terms</h2>
    <div class="content-wrapper">
      <h3 class="sub-title">{{appName}} Data Protection Statement</h3>
      <p>
        When you access and use our mobile application, we may collect and process your personal data.
        This statement fully explains how we gather, utilize and disclose user information during your use of our platform services.
      </p>
      <br>
      <p class="remind">
        To better understand how we safeguard your personal data, please read this privacy statement carefully.
      </p>
      <br>
      <p class="list-head">This document covers the following contents:</p>
      <p>1. Collection and usage rules of your personal data</p>
      <p>2. Rules for sharing, transferring and public disclosure of personal data</p>
      <p>3. Usage of Cookies, third-party SDKs and related technologies</p>
      <p>4. Security protection measures for user personal data</p>
      <p>5. Ways for you to manage your personal data</p>
      <p>6. Processing rules for information of underage users</p>
      <p>7. Storage rules for your personal data</p>
      <p>8. Updates and notifications of this agreement</p>
      <p>9. Contact channels for inquiries</p>
      <br>

      <p class="section-title">Overview</p>
      <p>
        {{appName}} attaches great importance to user data security. We strictly keep all collected user information confidential
        and have built a comprehensive data protection system to deliver stable and trustworthy services to every user.
      </p>
      <br>

      <p class="section-title">1. Collection & Usage of Personal Data</p>
      <p>
        Personal data refers to all information recorded electronically or in other forms that can identify an individual or reflect personal activities.
        It includes name, date of birth, biometric data, residential address, contact details, chat records, account passwords,
        asset information, physical health data, transaction records and other related content.
      </p>
      <br>
      <p>
        Sensitive personal data means information that may threaten personal and property safety, damage personal reputation
        or cause unfair discrimination once leaked, illegally transmitted or abused. It includes biometric information,
        bank account details, chat content, asset status, location tracks, health records, transaction data
        and personal information of users aged 14 and below.
      </p>
      <br>

      <p class="emphasis">You acknowledge and agree to the following provisions:</p>
      <p>
        1. We develop diversified services to meet your daily needs. Different functions will collect different types and scopes of personal data,
        which shall be subject to the actual rules of each individual service module.
      </p>
      <p>
        2. We keep optimizing product experience and upgrading technical capabilities. When launching new or upgraded functions,
        we may need to collect new types of data or adjust existing data usage rules. We will explain relevant rules
        via statement updates, pop-up reminders or page prompts, and collect your data only after obtaining your explicit approval.
        You can contact us anytime if you have questions or suggestions.
      </p>
      <p>
        3. Data collected from one service module may be applied to other functions of our platform,
        such as personalized content recommendation, security alerts and user portrait analysis.
        If we use your data beyond the original authorized scope, we will inform you again and obtain your consent in advance.
      </p>
      <br>

      <p class="note">Special Reminder: </p>
      <p>
        Our platform provides user recommendation services. The nickname, age, gender, constellation, occupation, company,
        hometown, usual activity areas, personal signature, avatar, photos and public posts you published
        may be displayed and recommended to other users on the platform.
      </p>
      <br>

      <p class="section-title">Business Modules & Corresponding Collected Data</p>
      <p>
        In accordance with relevant industry regulations, {{appName}} is classified as a social interaction application.
        Its core service is to provide online instant messaging including text, images, voice and video.
        The necessary personal data collected by each function are specified as below:
      </p>
      <br>
      <p>
        1. Account Registration: To complete account registration, you need to provide your email address, nickname, gender,
        country and date of birth as well as personal avatar. These data are used to create your account and enable social features.
        Meanwhile, we will collect device information such as device model, system version, device identification codes,
        network status, application list and sensor data to ensure stable operation of our services.
      </p>
      <p>
        2. Chat Service: Basic chat functions do not require extra personal data. We will collect your account information
        to maintain normal service operation and account security. When sending pictures, voice messages or initiating video calls,
        the app will request access to your album and microphone. You may refuse these permissions, which will not affect basic chat functions.
      </p>
      <p>
        3. Video Call: This function requires camera and microphone permissions. You cannot use video call normally without authorizing these permissions.
      </p>
      <p>
        4. Moments Browsing: No personal data or extra system permissions are required to view content in user moments.
      </p>
      <p>
        5. User Homepage Access: Viewing other users' albums and videos needs no special permissions.
        Initiating video calls on user homepage requires camera and microphone authorization.
      </p>
      <p>
        6. Profile Editing: When updating your avatar, the app will request album access permission.
        Refusing this permission will not affect the use of basic application functions.
      </p>
      <p>
        7. Payment Service: The payment function is jointly provided by our third-party payment partners.
        We will not obtain any of your payment-related data. Disabling relevant permissions will only make payment unavailable,
        while other basic functions can be used normally.
      </p>
      <p>
        8. Message Notification: We will apply for startup permission to push real-time message reminders.
        You can turn off this permission anytime in your mobile system settings.
      </p>
      <br>

      <p>
        All system permissions including camera, album and microphone are disabled by default.
        We will pop up independent authorization windows when you use related functions.
        You can enable or disable permissions freely in system settings. Closing permissions means you withdraw authorization,
        and we will stop collecting corresponding data and suspend related services.
        Closing permissions will not affect data collected previously with your authorization.
      </p>
      <br>

      <p>
        We may send service notifications, security verification messages and user research questionnaires to your reserved email address.
        If you publish other people's personal information on our platform, you must ensure that you have obtained legal authorization in advance.
      </p>
      <br>

      <p>
        Your submitted information will be used during your use of services. After you cancel your account or stop using certain functions,
        we will stop using your data and perform anonymization or deletion processing in compliance with legal requirements.
      </p>
      <br>

      <p class="section-title">Usage of Collected Data</p>
      <p>
        We only use your data for the purposes stated in this statement. If we need to use data for other purposes,
        we will obtain your prior consent. When we acquire data from third-party platforms, we will require the third party
        to collect information legally with user consent and guarantee the legality of data sources.
        We adopt the same high-standard security protection for externally obtained data as our own user data.
      </p>
      <br>

      <p class="emphasis">Exceptions to Authorization Requirements</p>
      <p>
        Under the following circumstances, we do not need your consent to collect and use personal data,
        and we may not accept your requests for modification, deletion or withdrawal of consent:
      </p>
      <p>1. To perform obligations stipulated by laws and regulations</p>
      <p>2. Matters related to national security and national defense</p>
      <p>3. For public safety, public health and general public interests</p>
      <p>4. For criminal investigation, litigation, trial and judgment enforcement</p>
      <p>5. To protect legitimate rights and interests of individuals when it is impractical to obtain consent</p>
      <p>6. Information that you have voluntarily disclosed to the public</p>
      <p>7. Data collected from legally public information channels</p>
      <br>

      <p>
        We will process collected data via desensitization, statistical analysis and algorithm training to optimize products and services.
        Processed anonymous data cannot be used to identify specific individuals, so no additional notification or consent is required.
        If we terminate platform operation, we will stop data collection immediately, issue service termination announcements,
        and delete or anonymize all retained personal data.
      </p>
      <br>

      <p class="section-title">Content Published by You</p>
      <p>
        You can share texts, pictures and other content with other users via our platform, which may contain your personal or sensitive data.
        Please think carefully before publishing private information. You can delete your published content at any time,
        but please note that such content may be saved by other users or independent third parties beyond our control.
      </p>
      <br>

      <p class="section-title">2. Sharing, Transfer & Public Disclosure of Personal Data</p>
      <p class="sub-heading">Data Sharing</p>
      <p>
        We will not share your personal data with external companies, organizations or individuals except for the following situations:
      </p>
      <p>1. Data sharing with your explicit authorization;</p>
      <p>2. Data sharing required by laws, judicial procedures or official orders;</p>
      <p>3. Sharing necessary data with affiliated enterprises to provide joint services and protect user security.
        We will re-apply for your consent if sensitive data is involved or data usage purposes are changed;</p>
      <p>4. Providing necessary data to authorized service partners, who can only use data to complete entrusted work.</p>
      <br>

      <p class="sub-heading">Data Transfer</p>
      <p>
        We will not transfer your personal data to any external party unless:
      </p>
      <p>1. You have given explicit consent to data transfer;</p>
      <p>2. In case of merger, acquisition or bankruptcy liquidation. The new data holder must abide by this statement;
        otherwise, they need to obtain your re-authorization.</p>
      <br>

      <p class="sub-heading">Public Disclosure</p>
      <p>1. Public disclosure based on your explicit consent or active operation;</p>
      <p>2. Disclosure in accordance with laws and platform rules to safeguard public interests when you violate relevant regulations.</p>
      <br>

      <p class="section-title">3. Application of Cookies and Third-Party SDKs</p>
      <p class="sub-heading">Cookies</p>
      <p>
        We may store small data files named Cookies on your device to ensure normal service operation,
        improve browsing experience and push content you may be interested in. You can clear Cookies in browser settings,
        but some dependent functions may become unavailable after clearing.
      </p>
      <br>

      <p class="sub-heading">Third-Party SDK</p>
      <p>
        Partial functions are realized by third-party SDK services. Third-party providers will collect data independently,
        and we will supervise their permission application and data collection behavior. The main third-party SDKs we integrate include:
        Google Firebase Push SDK, Third-party Payment SDK, NetEase Yunxin SDK, Shengwang SDK, AppsFlyer SDK,
        Facebook SDK, Huawei Push SDK. Refusing relevant permissions will only disable corresponding third-party functions,
        and will not affect other normal functions of {{appName}}.
      </p>
      <br>

      <p class="section-title">4. Personal Data Security Protection</p>
      <p>
        We strictly follow laws and industry standards to protect the confidentiality of your information:
      </p>
      <p>
        1. We adopt encryption, desensitization, access control and other technical means to prevent unauthorized access,
        leakage, damage and loss of personal data. We also organize regular training for employees on data protection.
      </p>
      <p>
        2. We have built a full life-cycle data security management system. Once a data security incident occurs,
        we will launch emergency plans immediately and notify users via official announcements or message pushes.
      </p>
      <p>
        3. We only collect necessary data and retain your information only within the period required for service purposes,
        unless extended by legal provisions.
      </p>
      <p>
        4. The internet cannot be completely secure. Please do not send sensitive personal information through unofficial channels.
        If you find your account or password has been leaked, please contact our customer service in time.
      </p>
      <br>

      <p class="section-title">5. Manage Your Personal Data</p>
      <p class="sub-heading">Data Query</p>
      <p>
        You have the right to view your personal data within the scope permitted by law.
        You can check and modify your information via <strong>[Mine - Top Right - Settings]</strong>.
        To edit nickname, avatar, birth date and personal introduction, log in and enter personal center by clicking your avatar.
      </p>
      <br>

      <p class="sub-heading">Data Correction</p>
      <p>
        If you find errors in your personal data, you can apply for correction and supplement through the above entry.
      </p>
      <br>

      <p class="sub-heading">Data Deletion</p>
      <p>
        You can delete part of your personal data independently. You may also apply for data deletion in the following cases:
      </p>
      <p>1. Our data processing behavior violates relevant laws;</p>
      <p>2. Data is collected and used without your explicit consent;</p>
      <p>3. We seriously violate the agreed terms;</p>
      <p>4. You stop using services or cancel your account;</p>
      <p>5. We permanently stop providing services for you.</p>
      <br>

      <p class="sub-heading">Adjust Authorization Scope</p>
      <p>
        For additional data collection permissions, you can enable or withdraw authorization by yourself,
        or contact our customer service team for help.
      </p>
      <br>

      <p class="section-title">6. Protection for Underage Users</p>
      <p class="forbid-text">This application prohibits users under 18 years old from registering and using.</p>
      <p>
        Our services are not designed for users under 18 years old. We will not actively collect or sell personal information of minors.
        If we accidentally obtain data from underage users, we will delete such information immediately.
        If we adjust relevant rules in the future, we will obtain verifiable consent from guardians before collecting minors' information.
      </p>
      <br>

      <p class="section-title">7. Data Storage Rules</p>
      <p>
        Our service servers are deployed in multiple regions worldwide. Your personal data may be stored and processed on overseas servers.
        You agree to cross-regional transmission and storage of your data for the purposes stated in this statement.
      </p>
      <br>

      <p class="section-title">8. Agreement Updates & Notifications</p>
      <p>
        We reserve the right to revise this privacy statement. You can always check the latest version via the link on our official website.
        We will release announcements for major content changes. Your continued use of our services after updates means
        you accept the revised terms. If you disagree with the updated content, please stop using our services.
      </p>
      <br>

      <p class="section-title">9. Contact Us</p>
      <p>
        If you have any questions about this privacy statement or data protection issues,
        please send emails to: <span class="email">{{appName}}@{{appName}}.site</span>
      </p>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {};
  },
  created() {}
};
</script>

<style scoped>
/* 全新样式，与原样式完全区分 */
.privacy-container {
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  background-color: #f5f7fa;
  min-height: 100vh;
  font-family: Arial, sans-serif;
}

.title {
  font-size: 24px;
  color: #222;
  text-align: center;
  margin: 0 0 20px 0;
  font-weight: 600;
}

.content-wrapper {
  max-width: 900px;
  margin: 0 auto;
  background: #ffffff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.1);
  font-size: 15px;
  line-height: 1.7;
  color: #333;
}

.sub-title {
  font-size: 18px;
  color: #2563eb;
  margin: 0 0 15px 0;
  text-align: center;
  font-weight: 500;
}

.remind {
  color: #d97706;
  font-weight: 500;
  padding: 8px 12px;
  background-color: #fffbeb;
  border-radius: 4px;
}

.list-head {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.section-title {
  font-size: 17px;
  color: #1e40af;
  font-weight: 600;
  margin: 24px 0 10px 0;
  padding-bottom: 4px;
  border-bottom: 1px solid #e5e7eb;
}

.sub-heading {
  font-size: 16px;
  color: #374151;
  font-weight: 500;
  margin: 12px 0 6px 0;
}

.emphasis {
  font-weight: 600;
  color: #b91c1c;
}

.note {
  font-weight: 600;
  color: #b91c1c;
}

.forbid-text {
  font-weight: 600;
  color: #dc2626;
  font-size: 16px;
}

.email {
  color: #2563eb;
  font-weight: 500;
}
</style>